/**
 * **Manage access…** of one Data Lake path (S8 Task 8.11): owner, owning group, access and (directories) default
 * entries with rwx, masks kept unless the user changes them (E5), the Effective column and a preview of every change.
 * Save = `hns-set-acl` with the loaded ETag (`ifMatch`, the path's identity) AND the loaded ACL (`baseline` — main
 * compares it with a fresh read right before the write, NOTES row A5). The loaded version is the baseline the draft is
 * coupled to: a background refetch (another dialog, a recursive job) replaces an UNCHANGED draft only; a changed draft
 * keeps its baseline and Save stays off until Reload. A read-only connection gets a view-only editor; the account key
 * (a super-user, not subject to ACLs) gets the banner instead of role hints.
 */
import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { RandomDotmSpinner } from '../../../../../components/ui/RandomDotmSpinner';
import { STORAGE_READ_ONLY_REASON } from '../../../../../core/engines/azureStorageRefusal';
import type { StorageHnsAccessControl } from '../../../../../types/azureStorage';
import { useReportStorageDialogGuard } from '../../properties/dialogs/storageDialogStore';
import { QueuedDialogNotice } from '../../properties/dialogs/QueuedDialogNotice';
import { aclEditFlags, draftFromAcl, hnsPathLabel, isDraftDirty, type AclEditorDraft } from '../aclDraft';
import { useHnsAccessControl } from '../hooks/useHnsAccessControl';
import { useHnsAclSave } from '../hooks/useHnsAclSave';
import { aclEditSource, outcomeOfError, trackAclEdit } from './aclEditAnalytics';
import { AclEditorForm } from './AclEditorForm';
import { AclSaveFeedback } from './AclSaveFeedback';
import { feedbackBlocksSave, type AclEditorFeedback } from './aclEditorFeedback';
import type { HnsHostedDialogProps } from './hnsDialogTypes';

export const SUPER_USER_BANNER = "This connection signs with the account key, so access control lists are not enforced for it; changes are made as the account's super-user.";

const sameAcl = (a: StorageHnsAccessControl, b: StorageHnsAccessControl): boolean => !isDraftDirty(draftFromAcl(b), a);

export function AclEditorDialog({ connectionId, resource, options, readOnly, onGuardChange, onClose }: HnsHostedDialogProps) {
  const query = useHnsAccessControl({ connectionId, resource });
  const saver = useHnsAclSave(connectionId);
  const [baseline, setBaseline] = useState<{ acl: StorageHnsAccessControl; version: number }>();
  const [dirty, setDirty] = useState(false);
  const [feedback, setFeedback] = useState<AclEditorFeedback>();
  useReportStorageDialogGuard(onGuardChange, dirty, saver.pending);
  useEffect(() => {
    const data = query.data;
    if (!data) return;
    setBaseline((current) => (!current || (!dirty && !sameAcl(current.acl, data)) ? { acl: data, version: (current?.version ?? 0) + 1 } : current));
  }, [query.data]); // eslint-disable-line react-hooks/exhaustive-deps -- a refetch must not re-run on local edits
  const serverMoved = !!baseline && !!query.data && !sameAcl(baseline.acl, query.data);
  const shown: AclEditorFeedback | undefined = feedback ?? (serverMoved ? { kind: 'server-changed' } : undefined);
  const lockedReason = readOnly ? STORAGE_READ_ONLY_REASON : undefined;
  const onDirtyChange = useCallback((next: boolean) => setDirty(next), []);

  const close = () => { saver.cancel(); onClose(); };
  const reload = () => { setFeedback(undefined); setBaseline(undefined); setDirty(false); query.reload(); };
  const save = async (draft: AclEditorDraft) => {
    if (!baseline || readOnly) return;
    setFeedback(undefined);
    const flags = aclEditFlags(baseline.acl, draft);
    const source = aclEditSource(options.origin);
    const result = await saver.save(baseline.acl, draft);
    if (result.kind === 'stale') return;
    if (result.kind === 'saved') {
      trackAclEdit({ recursive: 'none', outcome: 'ok', ...flags, source });
      onClose();
      return;
    }
    trackAclEdit({ recursive: 'none', outcome: outcomeOfError(result.kind, result.error.category), ...flags, source });
    setFeedback(result);
    if (result.kind === 'acl-changed') query.refresh();
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hns-acl-dialog">
        <DialogHeader>
          <DialogTitle>Manage access</DialogTitle>
          <DialogDescription>{resource.isDirectory ? 'Directory' : 'File'} <span className="font-mono">{hnsPathLabel(resource)}</span></DialogDescription>
        </DialogHeader>
        <QueuedDialogNotice />
        {baseline?.acl.superUser && <p className="rounded bg-muted p-2 text-xs" data-testid="hns-acl-super-user">{SUPER_USER_BANNER}</p>}
        {readOnly && <p className="text-xs text-muted-foreground" data-testid="hns-acl-read-only">{STORAGE_READ_ONLY_REASON}</p>}
        {!baseline && query.isLoading && <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="hns-acl-loading"><RandomDotmSpinner size={14} /> Loading access control…</div>}
        {!baseline && query.error && (
          <div className="grid gap-2 text-xs text-destructive" role="alert" data-testid="hns-acl-read-error">
            <span>{query.error.message}</span>
            <Button type="button" variant="outline" size="xs" className="w-fit" onClick={query.reload}>Retry</Button>
          </div>
        )}
        {shown && baseline && <AclSaveFeedback feedback={shown} loaded={baseline.acl} server={query.data} dirty={dirty} onReload={reload} />}
        {baseline && (
          <AclEditorForm key={baseline.version} acl={baseline.acl} lockedReason={lockedReason} saving={saver.pending}
            blockedReason={feedbackBlocksSave(shown) ? 'Reload before saving' : undefined} onDirtyChange={onDirtyChange} onSave={(draft) => void save(draft)} onCancel={close} />
        )}
      </DialogContent>
    </Dialog>
  );
}
